import React, { useEffect, useState , useMemo } from 'react'
import { Box, Divider, Skeleton, Typography } from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles'
import dayjs from 'dayjs';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';

import { useServices } from 'contexts';
import { getRecentCaseMasterAttempts } from 'services';
import { AttemptStates, firstTagName, parseCasebook, toTagLabel } from './constant';
import { WhiteBox, WhiteBoxHeader } from 'components/AppShell';
import { NoResultComponent } from 'components/Home/TodoList';
import { Session } from 'utils';
import { useNavigateWithClient } from 'hooks';
import CustomButton from 'components/CustomButton';
import Chip from 'components/Chip';
import Pagination from 'components/Pagination';
import StartAssessmentDialog from 'dialogs/StartAssessmentDialog';
import NoAccessTooltip, { NO_BATCH_ACCESS_MESSAGE } from 'components/NoAccessTooltip';
import { useSelectedBatchRemoved } from 'announcementStore';
import { useCaseMasterAccess } from './CaseMasterAccessContext';
import CaseMasterLockMessage from './CaseMasterLockMessage';

const useStyles = makeStyles((theme) => ({
  // Pagination caps the list at PAGE_SIZE cards, so the box grows to fit them
  // instead of scrolling inside a fixed height.
  box: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(5),
    marginTop: theme.spacing(5)
  },
  cardContainer: {
    display: 'flex',
    borderRadius: theme.spacing(2),
    width: '100%',
  },
  category: {
    width: '82px',
    height: '82px',
    backgroundColor: theme.palette.neutral['clr-50'],
    borderRadius: theme.spacing(2, 0, 0, 2),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  // An attempt with no feedback carries no buttons, so its card is short enough
  // that a fixed 82px panel stops short of the bottom and leaves a notch of
  // white inside the border. Stretching the panel keeps the icon centred while
  // the colour runs the full height.
  categoryStretched: {
    height: 'auto',
    alignSelf: 'stretch',
  },
  content: {
    padding: theme.spacing(3, 6),
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%'
  },
  topicDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  bottomContainer: {
    padding: theme.spacing(2),
    display: "flex",
    justifyContent: "space-between",
    alignItems: 'center',
    marginTop: theme.spacing(2),
    background: `linear-gradient(360deg, #FFFFFF 80%, rgba(255, 255, 255, 0) 100%)`,
    position: 'relative',
    bottom: '0px',
  }
}));

const MAX_SCORE = 10;
const PAGE_SIZE = 6;

export const AttemptCard = ({ attempt }) => {
  const classes = useStyles();
  const theme = useTheme();
  const navigateWithClient = useNavigateWithClient();

  const removed = useSelectedBatchRemoved();

  const attemptState = attempt.status;
  const [startInterview, setStartInterview] = React.useState(false);
  // `include=casemasterProblem` populates this as an array; elsewhere it is a
  // plain object. Read unwrapped, the same way the feedback page reads it -
  // left as an array, the title and the tag row below silently render nothing,
  // which is what leaves an unfinished attempt with only a date on it.
  const caseMaster = Array.isArray(attempt?.casemasterProblem)
    ? attempt.casemasterProblem[0]
    : attempt?.casemasterProblem;

  // This card is an already-completed attempt, so a free user has spent their
  // single attempt and an expired Pro plan blocks reattempts. Batch removal
  // takes priority over the upgrade message.
  const { getLockInfo } = useCaseMasterAccess();
  const { locked, message: lockMessage, upgradeUrl, linkText } = getLockInfo(caseMaster?._id);
  const disabled = removed || locked;
  const tooltipMessage = removed
    ? NO_BATCH_ACCESS_MESSAGE
    : <CaseMasterLockMessage
      message={lockMessage} upgradeUrl={upgradeUrl} linkText={linkText}
    />;

  // The feedback page prefers summary.overallScore and falls back to a computed
  // value; only the stored score is available on this list endpoint.
  const score = useMemo(() => {
    const raw = attempt?.summary?.overallScore ?? attempt?.overallScore;

    if (typeof raw !== 'number' || Number.isNaN(raw)) return null;

    // Clamped the same way as the feedback page's score display.
    return Math.min(Math.max(raw, 1), MAX_SCORE);
  }, [attempt]);

  const showScore = attemptState === AttemptStates.FEEDBACK_GENERATED && score !== null;

  // Stored out of MAX_SCORE and shown that way, e.g. "8.5/10". One decimal
  // rather than two so it fits the badge on a single line.
  const scoreOutOfMax = showScore ? `${score.toFixed(1)}/${MAX_SCORE}` : null;

  // The same row the all-cases screen carries, in the same order and read the
  // same way. Each entry is optional and is dropped when absent: only 17 cases
  // name a company and 109 a job role.
  //
  // Industry is the exception - it prefers the tag but falls back to
  // metadata.industry, which all 359 cases have, so this row keeps an entry
  // even for a case that was never tagged.
  const tagRow = [
    {
      key: 'company',
      Icon: ApartmentOutlinedIcon,
      label: toTagLabel(firstTagName(caseMaster?.tags?.company)),
    },
    {
      key: 'industry',
      Icon: FactoryOutlinedIcon,
      label: toTagLabel(
        firstTagName(caseMaster?.tags?.industry) || caseMaster?.metadata?.industry || ''
      ),
    },
    {
      key: 'jobRole',
      Icon: WorkOutlineOutlinedIcon,
      label: toTagLabel(firstTagName(caseMaster?.tags?.jobRole)),
    },
  ].filter(({ label }) => label);

  // Named in full, batch included - "IIM A (24-25)" - matching how the
  // all-cases screen marks a case that came from a real casebook.
  const casebookLabel = parseCasebook(caseMaster?.metadata?.casebook).label;

  // A handful of attempts point at a case that no longer resolves, which leaves
  // nothing for this row to say. It is left out rather than sitting empty and
  // holding the gap above it open. The row is otherwise shown whatever state
  // the attempt is in - an unfinished one describes its case just as fully as
  // a completed one does.
  const hasTagRow = tagRow.length > 0 || Boolean(casebookLabel);

  const stretchCategory = attemptState === AttemptStates.FEEDBACK_SKIPPED
    || attemptState === AttemptStates.NOT_APPLICABLE;

  const attemptedOn = attemptState === AttemptStates.NOT_APPLICABLE
    ? 'N/A'
    : dayjs(attempt?.startedAt).format('MMM DD, YYYY');

  const colors = useMemo(() => {
    const borderColors = {
      [AttemptStates.FEEDBACK_SKIPPED]: '#BF7900',
      [AttemptStates.NOT_APPLICABLE]: '#E2483C',
    };

    const baseColor = borderColors[attemptState] || theme.palette.neutral['clr-200'];
    const hoverColor = borderColors[attemptState] || theme.palette.primary['clr-100'];

    return { baseColor, hoverColor };
  }, [attemptState, theme]);

  const getFeedbackMessage = () => {
    switch (attemptState) {
      case AttemptStates.FEEDBACK_SKIPPED:
        return 'Feedback isn\'t available for this attempt as the interview was not completed';
      case AttemptStates.NOT_APPLICABLE:
        return 'This attempt was ended before the interview started';
      default:
        return null;
    }
  };

  const FeedbackNote = () => {
    const message = getFeedbackMessage();
    if (!message) return null;

    let color;
    switch (attemptState) {
      case AttemptStates.FEEDBACK_SKIPPED:
        color = '#BF7900';
        break;
      case AttemptStates.NOT_APPLICABLE:
        color = '#E2483C';
        break;
      default:
        color = theme.palette.neutral['clr-700'];
    }

    return (
      <Typography variant='body02-bold' style={{ color }}>
        {message}
      </Typography>
    );
  };

  const RenderButtons = () => {
    switch (attemptState) {
      case AttemptStates.FEEDBACK_GENERATED:
        return (
          <>
            <CustomButton
              onClick={() => navigateWithClient(`/performance/case-master/${attempt._id}`)}
              variant='outlined'
            >
              View feedback
            </CustomButton>
            <NoAccessTooltip show={disabled} message={tooltipMessage}>
              <CustomButton
                onClick={() => setStartInterview(true)}
                disabled={disabled}
                startIcon={locked ? <LockOutlinedIcon sx={{ fontSize: '18px' }} /> : undefined}
              >
                Reattempt to improve
              </CustomButton>
            </NoAccessTooltip>
          </>
        );
      case AttemptStates.FEEDBACK_SKIPPED:
      case AttemptStates.NOT_APPLICABLE:
        return null;
      default:
        return null;
    }
  };

  return (
    <Box
      className={classes.cardContainer}
      sx={{
        border: `2px solid ${colors.baseColor}`,
        '&:hover': {
          border: `2px solid ${colors.hoverColor}`,
        },
      }}
    >
      <Box
        className={
          `${classes.category} ${stretchCategory ? classes.categoryStretched : ''}`
        }
      >
        <Box
          sx={{
            backgroundColor: theme.palette.shades['clr-white-900'],
            borderRadius: theme.spacing(3),
            width: 56,
            height: 56,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            border: '1px solid #CCD4DE'
          }}
        >
          {
            showScore
              ? <>
                <Typography variant='body02-bold' color='neutral.clr-900'>
                  {scoreOutOfMax}
                </Typography>
                <Typography variant='overline-bold' color='neutral.clr-500'>
                  Score
                </Typography>
              </>
              : <img
                src='https://assets.languify.in/images/caseIcon.png'
                alt='case study' width={40} height={40}
              />
          }
        </Box>
      </Box>

      <Box className={classes.content}>
        <Box className={classes.topicDetails}
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Box display='flex' alignItems='flex-start' gap='16px'>
            <Typography
              variant='h5-medium'
              color='neutral.clr-900'
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                minWidth: 0,
              }}
            >
              {caseMaster?.title || 'Case study'}
            </Typography>
            <Chip
              content={attemptedOn}
              bgColor={theme.palette.neutral['clr-100']}
              color={theme.palette.neutral['clr-600']}
              typographyVariant='overline-bold'
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            />
          </Box>

          {
            hasTagRow &&
            <Box display='flex' alignItems='center' flexWrap='wrap' gap='20px'>
              {tagRow.map(({ key, Icon, label }) => (
                <Box key={key} display='flex' alignItems='center' gap='6px'>
                  <Icon sx={{ fontSize: '20px', color: 'neutral.clr-400' }} />
                  <Typography variant='body01-medium' color='neutral.clr-500'>
                    {label}
                  </Typography>
                </Box>
              ))}
              {
                // Only marked when the case actually names a casebook, which the
                // generated ones never do.
                casebookLabel &&
                <Box display='flex' alignItems='center' gap='6px'>
                  <VerifiedOutlinedIcon sx={{ fontSize: '20px', color: 'primary.clr-300' }} />
                  <Typography variant='body01-semiBold' color='primary.clr-300'>
                    {casebookLabel}
                  </Typography>
                </Box>
              }
            </Box>
          }

          <FeedbackNote />
        </Box>

        <Box display='flex' gap='12px' alignItems='center'>
          <RenderButtons />
        </Box>
      </Box>
      <StartAssessmentDialog
        onClose={() => setStartInterview(false)}
        open={startInterview}
        template={{
          _id: attempt.casemasterProblem?._id,
          name: attempt.casemasterProblem?.title,
          type: 'case_master'
        }}
        codingInterview={false}
        stateParams={attempt.assessmentEntryPoint}
      />
    </Box>
  );
};

const RecentAttempts = ({ onAttemptStatusCheck }) => {
  const service = useServices();
  const classes = useStyles();

  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const fetchRecentAttempts = async () => {
      try {
        setLoading(true);
        const { attempts: attemptList, total } = await service.callService(
          getRecentCaseMasterAttempts,
          {
            include: "casemasterProblem",
            user: Session.userId,
            pageNumber: page + 1,
            pageSize: PAGE_SIZE,
            includeTotal: true
          }
        );

        setAttempts(attemptList);
        setTotalCount(total);

        if (page === 0) checkFirstFiveAttempts(attemptList);
      } catch (err) {
        console.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentAttempts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const checkFirstFiveAttempts = (attemptList) => {
    let hasPending = false;
    let hasSkipped = false;

    for (let i = 0; i < Math.min(5, attemptList.length); i++) {
      const attempt = attemptList[i];
      if (attempt.status === AttemptStates.FEEDBACK_SKIPPED) {
        hasSkipped = true;
      }
    }

    if (onAttemptStatusCheck) {
      onAttemptStatusCheck({ hasPending, hasSkipped });
    }
  };

  return (
    <WhiteBox>
      <WhiteBoxHeader
        id="recent-attempts"
        heading="Recent attempts"
        subheading="History of all your case interview attempts"
      />
      <Divider />
      <Box className={classes.box}>
        {
          loading
            ? Array.from({ length: 3 }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  border: '2px solid #E3EAF3',
                  borderRadius: 2,
                  width: '100%',
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    width: '82px',
                    height: '82px',
                    backgroundColor: '#F8F9FB',
                    borderRadius: '16px 0 0 16px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mt: 3,
                    ml: 3,
                  }}
                >
                  <Skeleton variant="circular" width={56} height={56} />
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    padding: 3,
                    pl: 6,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
                    <Skeleton width="70%" height={24} sx={{ mb: 1 }} />
                    <Skeleton width="40%" height={20} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Skeleton variant="rectangular" width={120} height={36} />
                    <Skeleton variant="rectangular" width={120} height={36} />
                  </Box>
                </Box>
              </Box>
            ))
            :
            attempts?.length ? (
              attempts.map((attempt, index) => (
                <AttemptCard
                  key={index}
                  attempt={attempt}
                />
              ))
            ) : (
              <NoResultComponent
                description={
                  <>
                    Nothing to show here! <br />
                    Attempt any case interview challenge to see your progress.
                  </>
                }
                image='https://assets.languify.in/images/noPreparation.png'
              />
            )
        }
      </Box>

      {totalCount > PAGE_SIZE && (
        <Box className={classes.bottomContainer}>
          <Typography variant='body01-bold' color='neutral.clr-900'>
            Attempts: {(page * PAGE_SIZE) + 1}-{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount}
          </Typography>
          <Pagination
            count={Math.ceil(totalCount / PAGE_SIZE)}
            page={page}
            setPage={setPage}
          />
        </Box>
      )}
    </WhiteBox>
  );
};

export default RecentAttempts
